import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountStatusesEnum } from 'dash-common';
import { Box, DropDown, Icon, ModalContext, toast } from 'dash-ui';

import {
  SaleTeamUser,
  GetSaleTeamUsersDocument,
  useSendRegistrationLinkToDeveloperMutation,
} from 'gql/generated.graphql';
import { ExternalSalesModalKeys } from 'pages/external-sales/constants/constants';

const SaleUsersTableRowActions = ({ user }: { user: SaleTeamUser }) => {
  const { t } = useTranslation(['common', 'tables']);
  const { toggleModal, setPayload } = useContext(ModalContext);

  const [sendRegistrationLinkToUser, { loading }] =
    useSendRegistrationLinkToDeveloperMutation({
      onError: (error) => toast.error(error.toString()),
      onCompleted: () =>
        toast.success(t('common:alerts.invitationHasBeenSent')),
    });

  const sendItemOnClick = () => {
    sendRegistrationLinkToUser({
      variables: { accountId: user.account._id },
      refetchQueries: [GetSaleTeamUsersDocument],
    });
  };

  return (
    <Box w={16}>
      <DropDown
        data-testid="actions"
        placementHorizontal="left"
        Anchor={() => <Icon name="dots" />}
        items={[
          {
            key: 'edit',
            icon: 'edit',
            w: 170,
            children: t('common:labels.viewEditAction'),
            contentCentered: false,
            onClick: () => {
              toggleModal(true, ExternalSalesModalKeys.EditSaleTeamUser);
              setPayload(user);
            },
          },
          {
            key: 'send',
            icon: 'send',
            children: t(
              `tables:organizationUsers.${
                user.account.status === AccountStatusesEnum.ADDED
                  ? 'sendInvitation'
                  : 'resendInvitation'
              }`
            ),
            iconProps: { size: 14 },
            disabled:
              user.account.status === AccountStatusesEnum.ARCHIVED ||
              user.account.status === AccountStatusesEnum.ACTIVE,
            contentCentered: false,
            w: 170,
            onClick: sendItemOnClick,
            isLoading: loading,
          },
          {
            key: 'archive',
            icon: 'archive',
            children: t('common:labels.archive'),
            w: 170,
            disabled: user.account.status !== AccountStatusesEnum.ACTIVE,
            contentCentered: false,
            onClick: () => {
              setPayload({
                status: AccountStatusesEnum.ARCHIVED,
                saleTeamUserId: user._id,
              });
              toggleModal(true, ExternalSalesModalKeys.SetSaleTeamUserStatus);
            },
          },
          {
            key: 'unarchive',
            icon: 'unarchive',
            children: t('common:labels.unarchive'),
            disabled: user.account.status !== AccountStatusesEnum.ARCHIVED,
            contentCentered: false,
            w: 170,
            onClick: () => {
              setPayload({
                status: AccountStatusesEnum.ACTIVE,
                saleTeamUserId: user._id,
              });
              toggleModal(true, ExternalSalesModalKeys.SetSaleTeamUserStatus);
            },
          },
          {
            key: 'delete',
            icon: 'close',
            w: 170,
            children: t('common:labels.delete'),
            contentCentered: false,
            onClick: () => {
              setPayload(user);
              toggleModal(true, ExternalSalesModalKeys.DeleteSaleTeamUser);
            },
          },
        ]}
      />
    </Box>
  );
};

export default SaleUsersTableRowActions;
